import axiosInstance from '../axiosInstance';


class UserService {
  /**
   * Get patient information by file number
   * @param {string} fileNumber - Patient file number
   * @returns {Promise<Object>} Patient data
   */
  static async getPatient(fileNumber) {
    if (!fileNumber?.trim()) {
      throw new Error('File number is required');
    }

    try {
      const { data } = await axiosInstance.get(`/auth/get-patient?file_number=${fileNumber.trim()}`);
      return data;
    } catch (error) {
      console.error('Get patient error:', error);
      throw error;
    }
  }

  /**
   * Send OTP to user's phone
   * @param {string} fileNumber - Patient file number
   * @param {string} phoneNumber - Patient phone number
   * @returns {Promise<Object>} OTP response data
   */
  static async sendOTP(fileNumber, phoneNumber) {
    if (!fileNumber?.trim() || !phoneNumber?.trim()) {
      throw new Error('File number and phone number are required');
    }

    try {
      const { data } = await axiosInstance.get(
        `/auth/send-otp?file_number=${fileNumber.trim()}&phone_number=${phoneNumber.trim()}`
      );
      return data;
    } catch (error) {
      console.error('Send OTP error:', error);
      throw error;
    }
  }

  /**
   * Verify OTP
   * @param {string} fileNumber - Patient file number
   * @param {string} phoneNumber - Patient phone number
   * @param {string} otp - OTP code
   * @returns {Promise<Object>} Verification response data
   */
  static async verifyOTP(fileNumber, phoneNumber, otp) {
    if (!fileNumber?.trim() || !phoneNumber?.trim() || !otp?.trim()) {
      throw new Error('File number, phone number, and OTP are required');
    }

    try {
      const { data } = await axiosInstance.get(
        `/auth/verify-otp?file_number=${fileNumber.trim()}&phone_number=${phoneNumber.trim()}&otp=${otp.trim()}`
      );
      return data;
    } catch (error) {
      console.error('Verify OTP error:', error);
      throw error;
    }
  }

  /**
   * Login user
   * @param {string} fileNumber - Patient file number
   * @param {string} password - User password
   * @param {string} patientId - Optional patient ID
   * @returns {Promise<Object>} Login response data
   */
  static async login(fileNumber, password, patientId =null,qrCode = null) {
    if (!fileNumber?.trim() || !password?.trim()) {
      throw new Error('File number and password are required');
    }

    try {
      const requestData = {
        file_number: fileNumber.trim(),
        password: password.trim(),
        patient_id: patientId || null,
        qr_code: qrCode || null,
      };

      const { data } = await axiosInstance.post('/auth/login', requestData);
      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  /**
   * Create password for user
   * @param {string} phoneNumber - Patient phone number
   * @param {string} fileNumber - Patient file number
   * @param {string} password - New password
   * @param {string} confirmPassword - Password confirmation
   * @returns {Promise<Object>} Create password response data
   */
  static async createPassword(phoneNumber, fileNumber, password, confirmPassword) {
    if (!phoneNumber?.trim() || !fileNumber?.trim() || !password?.trim() || !confirmPassword?.trim()) {
      throw new Error('All fields are required');
    }

    if (password !== confirmPassword) {
      throw new Error('Passwords do not match');
    }

    try {
      const requestData = {
        phone_number: phoneNumber.trim(),
        file_number: fileNumber.trim(),
        password: password.trim(),
        password_confirmation: confirmPassword.trim()
      };

      const { data } = await axiosInstance.post('/auth/create-password', requestData);
      return data;
    } catch (error) {
      console.error('Create password error:', error);
      throw error;
    }
  }

  /**
   * Phone/Email lookup for patients
   * @param {string} searchTerm - Phone number or email to search
   * @returns {Promise<Object>} Lookup response data
   */
  static async phoneLookup(searchTerm) {
    if (!searchTerm?.trim()) {
      throw new Error('Search term is required');
    }

    try {
      const requestData = {
        search_term: searchTerm.trim()
      };

      const { data } = await axiosInstance.post('/patients/phone-lookup', requestData);
      return data;
    } catch (error) {
      console.error('Phone lookup error:', error);
      throw error;
    }
  }

  /**
   * Initiate virtual QR payment
   * @param {number} hospitalId - Hospital ID
   * @param {number} patientId - Patient ID
   * @param {string} patientFileNumber - Patient file number
   * @param {string} patientPhone - Patient phone number
   * @param {string} email - Patient email
   * @param {string} callbackUrl 
   * @returns {Promise<Object>} Payment initiation response data
   */
  static async initiateVirtualQrPayment(hospitalId, patientId, patientFileNumber, patientPhone, email, callbackUrl = null) {
    if (!hospitalId || !patientId || !patientFileNumber?.trim() || !patientPhone?.trim()) {
      throw new Error('Hospital ID, Patient ID, file number, and phone number are required');
    }

    try {
      const requestData = {
        hospital_id: hospitalId,
        patient_id: patientId,
        patient_file_number: patientFileNumber.trim(),
        patient_phone: patientPhone.trim(),
        email: email?.trim() || ''
      };

      if (callbackUrl?.trim()) {
        requestData.callback_url = callbackUrl.trim();
      }

      const { data } = await axiosInstance.post('/payments/virtual-qr/initiate', requestData);
      return data;
    } catch (error) {
      console.error('Virtual QR payment initiation error:', error);
      throw error;
    }
  }

  /**
   * Verify virtual QR payment
   * @param {string} reference - Payment reference
   * @returns {Promise<Object>} Payment verification response data
   */
  static async verifyVirtualQrPayment(reference) {
    if (!reference?.trim()) {
      throw new Error('Payment reference is required');
    }

    try {
      const { data } = await axiosInstance.get(`/payments/virtual-qr/verify/${reference.trim()}`);
      return data;
    } catch (error) {
      console.error('Virtual QR payment verification error:', error);
      throw error;
    }
  }

  /**
   * Logout user
   * @returns {Promise<Object>} Logout response data
   */
  static async logout() {
    try {
      const { data } = await axiosInstance.post('/auth/logout');
      return data;
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }
}

export default UserService;